// assets/province-cart-sync.js
(function () {
  const PROVINCE_KEY = "ageGateProvince";
  const LOCK_KEY = "provinceCartRebuiltFor"; // store province code
  const productCache = new Map();

  let inflight = false;

    async function forceXOcartRehydrate() {
      // Force Shopify cart to be up to date
      await fetch("/cart.js", { credentials: "same-origin" });

      // Tell XO to re-hydrate cart state
      document.dispatchEvent(
        new CustomEvent("xo:cart:hydrate", { bubbles: true })
      );

      // Also trigger known XO refresh hooks
      document.dispatchEvent(
        new CustomEvent("xo:cart:update", { bubbles: true })
      );
    }

  /* =========================
     HELPERS
  ========================= */

  async function forceCartSync() {
    // 1) Force Shopify cart to settle
    await fetch("/cart.js", { credentials: "same-origin" });

    // 2) Give XO a moment to react
    await new Promise(r => setTimeout(r, 150));
  }


  function getProvinceFromVariantTitle(title) {
    const m = title && title.match(/\((AB|MB|NB|ON|FED)\)$/);
    return m ? m[1] : null;
  }

  async function getCart() {
    const r = await fetch("/cart.js", { credentials: "same-origin" });
    return r.json();
  }

  async function getProductByHandle(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    const p = fetch(`/products/${handle}.js`, { credentials: "same-origin" })
      .then(r => (r.ok ? r.json() : null))
      .catch(() => null);

    productCache.set(handle, p);
    return p;
  }

  function findVariant(product, province) {
    const suffix = `(${province})`;
    return product?.variants?.find(v => v.title?.trim().endsWith(suffix)) || null;
  }

  function refreshXOcartUI() {
    // Shopify-style events
    document.dispatchEvent(new Event("cart:refresh"));
    document.dispatchEvent(new Event("cart:updated"));

    // XO lifecycle events (important)
    document.dispatchEvent(new CustomEvent("xo:cart:updated", { bubbles: true }));
    document.dispatchEvent(new CustomEvent("xo:cart:refresh", { bubbles: true }));
    document.dispatchEvent(new CustomEvent("xo:cart:rebuild", { bubbles: true }));

    // Hard refresh XO cart components
    document.querySelectorAll(
      "xo-cart, xo-cart-drawer, xo-mini-cart"
    ).forEach(el => {
      try {
        if (typeof el.refresh === "function") el.refresh();
        if (typeof el.open === "function") el.open();
      } catch (e) {}
    });
  }

  async function reloadCartSections() {
    try {
      const res = await fetch(
        `${window.Shopify.routes.root}?sections=cart-mini,cart-drawer`,
        { credentials: "same-origin" }
      );

      const sections = await res.json();

      // ---- MINI CART (SOURCE OF TRUTH) ----
      if (sections["cart-mini"]) {
        const miniDoc = new DOMParser().parseFromString(
          sections["cart-mini"],
          "text/html"
        );

        const newMini =
          miniDoc.querySelector("#CartMini, xo-cart-mini, .cart-mini");
        const currentMini =
          document.querySelector("#CartMini, xo-cart-mini, .cart-mini");

        if (newMini && currentMini) {
          currentMini.replaceWith(newMini);
        }
      }

      // ---- CART DRAWER ----
      if (sections["cart-drawer"]) {
        const drawerDoc = new DOMParser().parseFromString(
          sections["cart-drawer"],
          "text/html"
        );

        const newDrawer =
          drawerDoc.querySelector("#CartDrawer, xo-cart-drawer");
        const currentDrawer =
          document.querySelector("#CartDrawer, xo-cart-drawer");

        if (newDrawer && currentDrawer) {
          const wasOpen =
            document.documentElement.classList.contains("xo-cart-open") ||
            currentDrawer.hasAttribute("open");

          currentDrawer.replaceWith(newDrawer);

          // Re-open drawer if it was open
          if (wasOpen && typeof newDrawer.open === "function") {
            requestAnimationFrame(() => newDrawer.open());
          }
        }
      }

    } catch (e) {
      console.warn("[ProvinceCartSync] Section reload failed", e);
    }
  }



  /* =========================
     CORE (EXCHANGE CART)
     - keeps same qty
     - removes old province variants
     - avoids rerun on refresh
  ========================= */
  async function rebuildCartToProvince(province) {
    if (!province || inflight) return;
    inflight = true;

    try {
      // ✅ don’t rebuild again for same province during this session
      if (sessionStorage.getItem(LOCK_KEY) === province) return;

      const cart = await getCart();
      if (!cart?.items?.length) {
        sessionStorage.setItem(LOCK_KEY, province);
        return;
      }

      // 1) Build desired target quantities (variant_id => qty)
      const target = {};

      for (const item of cart.items) {
        const currentProvince =
          getProvinceFromVariantTitle(item.variant_title) || item.properties?._province;

        // If already correct province, keep same variant & qty
        if (currentProvince === province) {
          target[item.variant_id] = (target[item.variant_id] || 0) + item.quantity;
          continue;
        }

        // Otherwise, map to same product variant for the selected province
        const product = await getProductByHandle(item.handle);
        if (!product) continue;

        const v = findVariant(product, province);
        if (!v) continue; // product not available in that province

        target[v.id] = (target[v.id] || 0) + item.quantity;
      }

      // 2) IMPORTANT: also zero-out ALL current variants not in target
      // This is what was missing — without it, old items stay.
      const updates = {};

      for (const item of cart.items) {
        updates[item.variant_id] = 0;
      }

      // then apply desired quantities
      Object.keys(target).forEach(variantId => {
        updates[variantId] = target[variantId];
      });

      // 3) Atomic update (no doubles)
      const res = await fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates })
      });

      if (!res.ok) {
        console.warn("[ProvinceCartSync] /cart/update.js failed", res.status);
        return;
      }

      // lock + refresh UI
      sessionStorage.setItem(LOCK_KEY, province);

      // lock rebuild for this province
      sessionStorage.setItem(LOCK_KEY, province);

      // 🔥 THIS is what updates cart-mini + drawer in XO
      await forceXOcartRehydrate();


    } catch (e) {
      console.warn("[ProvinceCartSync] Error:", e);
    } finally {
      inflight = false;
    }
  }

  /* =========================
     EVENTS
  ========================= */
  window.addEventListener("province:changed", e => {
    const province = e?.detail?.province;
    if (!province) return;

    // province actually changed → allow rebuild again
    sessionStorage.removeItem(LOCK_KEY);
    rebuildCartToProvince(province);
  });
  /* =========================
     AUTO-RUN AFTER AGE GATE RELOAD
  ========================= */
  document.addEventListener("DOMContentLoaded", () => {
    const province = localStorage.getItem(PROVINCE_KEY);
    const justChanged = sessionStorage.getItem("ageGateProvinceJustChanged");

    if (!province || !justChanged) return;

    // Clean flag immediately (important)
    sessionStorage.removeItem("ageGateProvinceJustChanged");

    // Allow rebuild for this province
    sessionStorage.removeItem(LOCK_KEY);

    // 🔥 Trigger rebuild
    rebuildCartToProvince(province);
  });

  document.addEventListener("province:changed", async (e) => {
    const province = e.detail.province;

    console.log("[ProvinceCart] Province changed → rebuilding cart:", province);

    // 1️⃣ Force Shopify cart state
    await fetch("/cart.js", { credentials: "same-origin" });

    // 2️⃣ Trigger XO cart rebuild
    document.dispatchEvent(
      new CustomEvent("xo:cart:hydrate", { bubbles: true })
    );

    document.dispatchEvent(
      new CustomEvent("xo:cart:update", { bubbles: true })
    );
  });
/* =========================================================
   PUBLIC API: allow other scripts (AgeGate) to rebuild cart now
   ========================================================= */
(function () {
  window.ProvinceCartSync = window.ProvinceCartSync || {};

  // If you already have a rebuild function inside this file,
  // call it here. If not, this wrapper will still trigger XO refresh.
  // window.ProvinceCartSync.rebuildNow = async function (provinceCode) {
  //   try {
  //     console.log("[ProvinceCartSync] rebuildNow() for:", provinceCode);

  //     // ✅ If your file already has a real rebuild function, call it here:
  //     // Example (rename to your real function name):
  //     // await rebuildCartForProvince(provinceCode);

  //     // Fallback: at least re-sync Shopify cart + XO cart UI
  //     await fetch("/cart.js", { credentials: "same-origin" });

  //     document.dispatchEvent(new CustomEvent("xo:cart:hydrate", { bubbles: true }));
  //     document.dispatchEvent(new CustomEvent("xo:cart:update", { bubbles: true }));

  //     // Small settle time for XO UI
  //     await new Promise(r => setTimeout(r, 250));

  //     console.log("[ProvinceCartSync] rebuildNow() done");
  //     return true;
  //   } catch (e) {
  //     console.warn("[ProvinceCartSync] rebuildNow() failed:", e);
  //     return false;
  //   }
  // };
window.ProvinceCartSync.rebuildNow = async function (provinceCode) {
  try {
    console.log("[ProvinceCartSync] rebuildNow() for:", provinceCode);

    // 🔓 allow rebuild even if already run
    sessionStorage.removeItem(LOCK_KEY);

    // 🔥 CALL THE REAL LOGIC
    await rebuildCartToProvince(provinceCode);

    console.log("[ProvinceCartSync] rebuildNow() completed");
    return true;
  } catch (e) {
    console.warn("[ProvinceCartSync] rebuildNow() failed:", e);
    return false;
  }
};

  // ✅ Also listen for province changes (optional)
  document.addEventListener("province:changed", async (e) => {
    const province = e?.detail?.province;
    if (!province) return;
    await window.ProvinceCartSync.rebuildNow(province);
  });
})();

})();
